import { useMemo, useState } from 'react';
import { Filter, Plus, RefreshCw, Trash2 } from 'lucide-react';
import { Button } from '@/components/Button';
import { EmptyState, ErrorState, TableSkeleton } from '@/components/States';
import { Modal } from '@/components/Modal';
import {
  useRecountSegment,
  useSaveSegment,
  useSegmentPreview,
  useSegments,
} from '@/hooks/use-segments';
import { describeError } from '@/lib/describe-error';
import { formatDateTime } from '@/lib/utils';
import type {
  SegmentCondition,
  SegmentField,
  SegmentGroup,
  SegmentOperator,
  SegmentSummary,
} from '@/lib/schemas';

/**
 * Saved audience rules.
 *
 * The builder offers a CLOSED set of fields and operators, mirroring the compiler's enums. That is not
 * a limitation of the UI — the compiler only accepts what it can translate, so a free-text field here
 * would be a field the server has to reject.
 */
export function SegmentsPage() {
  const [editing, setEditing] = useState<{ segment?: SegmentSummary } | null>(null);

  const { data, isPending, isError, error, refetch } = useSegments();
  const recount = useRecountSegment();

  return (
    <div className="px-4 py-6 sm:px-6">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-xl font-semibold text-heading">Segments</h1>
          <p className="mt-1 text-sm text-muted">
            Who a campaign goes to. Evaluated at send time, so a segment is never stale.
          </p>
        </div>

        <Button variant="primary" onClick={() => setEditing({})}>
          <Plus className="h-4 w-4" aria-hidden="true" />
          New segment
        </Button>
      </div>

      <div className="mt-5 overflow-hidden rounded-lg border border-border bg-surface">
        {isPending ? (
          <TableSkeleton rows={5} />
        ) : isError ? (
          <ErrorState error={error} onRetry={() => void refetch()} />
        ) : data.items.length === 0 ? (
          <EmptyState
            icon={Filter}
            title="No segments yet"
            description="A segment is a rule over your contacts — everyone who consented in the last year, everyone who opened the last campaign, everyone on a plan. Build one and a campaign can use it."
            action={
              <Button variant="primary" onClick={() => setEditing({})}>
                <Plus className="h-4 w-4" aria-hidden="true" />
                New segment
              </Button>
            }
          />
        ) : (
          <ul className="divide-y divide-border">
            {data.items.map((segment) => (
              <li key={segment.id} className="flex flex-wrap items-center gap-3 px-4 py-3">
                <div className="min-w-0 flex-1">
                  <p className="truncate text-sm font-medium text-heading">{segment.name}</p>

                  <p className="mt-0.5 text-xs text-muted">
                    {segment.description ?? 'No description'}
                    {' · '}
                    {/* BOTH have to be present. Branching on the count alone rendered
                        "12,904 contacts as of —" for a row that had a count but no timestamp, and a
                        count you cannot date is not a count anybody should act on. */}
                    {segment.lastCount === null || segment.lastCountedAt === null
                      ? 'Never counted'
                      : `${segment.lastCount.toLocaleString()} contacts as of ${formatDateTime(segment.lastCountedAt)}`}
                  </p>
                </div>

                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => recount.mutate(segment.id)}
                  disabled={recount.isPending}
                >
                  <RefreshCw className="h-3.5 w-3.5" aria-hidden="true" />
                  Recount
                </Button>

                <Button variant="secondary" size="sm" onClick={() => setEditing({ segment })}>
                  Edit
                </Button>
              </li>
            ))}
          </ul>
        )}
      </div>

      {/* The cached count is advisory — every send recomputes it. Said here so nobody treats the
          number in the list as the number that will be mailed. */}
      <p className="mt-2 text-xs text-muted">
        Counts here are a snapshot. Every send resolves the segment again, so the audience is current
        at the moment it goes out.
      </p>

      {editing !== null && (
        <SegmentBuilderDialog
          existing={editing.segment}
          brandId={data?.items[0]?.brandId}
          onClose={() => setEditing(null)}
        />
      )}
    </div>
  );
}

/** Fields, grouped so the list is navigable rather than eighteen flat options. */
const fieldGroups: { label: string; fields: { value: SegmentField; label: string }[] }[] = [
  {
    label: 'Profile',
    fields: [
      { value: 'Email', label: 'Email address' },
      { value: 'FirstName', label: 'First name' },
      { value: 'LastName', label: 'Last name' },
      { value: 'CustomField', label: 'Custom field' },
    ],
  },
  {
    label: 'Consent',
    fields: [
      { value: 'ConsentSource', label: 'Consent source' },
      { value: 'ConsentAt', label: 'Consent date' },
      { value: 'ConfirmedAt', label: 'Double opt-in confirmed' },
      { value: 'CreatedAt', label: 'Added to the database' },
    ],
  },
  {
    label: 'Engagement',
    fields: [
      { value: 'LastOpenedAt', label: 'Last opened' },
      { value: 'LastClickedAt', label: 'Last clicked' },
      { value: 'LastDeliveredAt', label: 'Last delivered' },
      { value: 'ConsecutiveSoftBounces', label: 'Consecutive soft bounces' },
    ],
  },
];

type OperatorChoice = { value: SegmentOperator; label: string };

/**
 * Operators per field, mirroring SegmentFieldMetadata so the UI cannot offer an invalid pairing.
 *
 * The return type is a NON-EMPTY tuple, which is what lets callers read `[0]` to pick a default
 * without an assertion. Every branch below genuinely has a first element; saying so in the type is
 * better than asserting it at each call site.
 */
function operatorsFor(field: SegmentField): [OperatorChoice, ...OperatorChoice[]] {
  const text: [OperatorChoice, ...OperatorChoice[]] = [
    { value: 'Equals', label: 'is' },
    { value: 'NotEquals', label: 'is not' },
    { value: 'Contains', label: 'contains' },
    { value: 'StartsWith', label: 'starts with' },
    { value: 'IsSet', label: 'is set' },
    { value: 'IsNotSet', label: 'is not set' },
  ];

  const dates: [OperatorChoice, ...OperatorChoice[]] = [
    { value: 'WithinLastDays', label: 'in the last (days)' },
    { value: 'NotWithinLastDays', label: 'not in the last (days)' },
    { value: 'IsSet', label: 'has ever happened' },
    { value: 'IsNotSet', label: 'has never happened' },
  ];

  const numbers: [OperatorChoice, ...OperatorChoice[]] = [
    { value: 'Equals', label: 'is' },
    { value: 'GreaterThan', label: 'is more than' },
    { value: 'LessThan', label: 'is fewer than' },
  ];

  switch (field) {
    case 'ConsentAt':
    case 'ConfirmedAt':
    case 'CreatedAt':
    case 'LastDeliveredAt':
    case 'LastOpenedAt':
    case 'LastClickedAt':
      return dates;
    case 'ConsecutiveSoftBounces':
      return numbers;
    default:
      return text;
  }
}

/** True when the operator needs no value — the value input is hidden rather than disabled. */
function needsValue(operator: SegmentOperator): boolean {
  return operator !== 'IsSet' && operator !== 'IsNotSet' && operator !== 'IsTrue' && operator !== 'IsFalse';
}

function SegmentBuilderDialog({
  existing,
  brandId,
  onClose,
}: {
  existing?: SegmentSummary | undefined;
  brandId: string | undefined;
  onClose: () => void;
}) {
  const [name, setName] = useState(existing?.name ?? '');
  const [conditions, setConditions] = useState<SegmentCondition[]>([blankCondition()]);
  const [touched, setTouched] = useState(false);

  const save = useSaveSegment();

  // Memoised so the preview's query key is stable across renders that did not change the rule —
  // otherwise every keystroke anywhere in the dialog refetches.
  const rule = useMemo<SegmentGroup>(
    () => ({ combinator: 'And', conditions: conditions.filter(isUsable), groups: [] }),
    [conditions],
  );

  const preview = useSegmentPreview(existing?.brandId ?? brandId, rule);

  const nameError = name.trim() === '' ? 'Give the segment a name.' : undefined;
  const ruleError = rule.conditions.length === 0 ? 'Add at least one condition.' : undefined;

  return (
    <Modal
      title={existing === undefined ? 'New segment' : `Edit ${existing.name}`}
      description="Conditions combine with AND. The count updates as you build."
      onClose={onClose}
      footer={
        <>
          <Button variant="ghost" onClick={onClose} disabled={save.isPending}>
            Cancel
          </Button>

          <Button type="submit" form="segment-form" variant="primary" disabled={save.isPending}>
            {save.isPending ? 'Saving…' : 'Save segment'}
          </Button>
        </>
      }
    >
      <form
        id="segment-form"
        noValidate
        onSubmit={(event) => {
          event.preventDefault();
          setTouched(true);

          if (nameError !== undefined) {
            document.getElementById('segment-name')?.focus();
            return;
          }

          const targetBrandId = existing?.brandId ?? brandId;

          if (ruleError !== undefined || targetBrandId === undefined) return;

          save.mutate(
            {
              ...(existing === undefined ? {} : { segmentId: existing.id }),
              brandId: targetBrandId,
              name,
              description: null,
              rule,
            },
            { onSuccess: onClose },
          );
        }}
        className="flex flex-col gap-4"
      >
        <div>
          <label htmlFor="segment-name" className="mb-1 block text-sm font-medium text-heading">
            Name
          </label>

          <input
            id="segment-name"
            type="text"
            value={name}
            onChange={(event) => setName(event.target.value)}
            onBlur={() => setTouched(true)}
            aria-invalid={touched && nameError !== undefined}
            aria-describedby={touched && nameError !== undefined ? 'segment-name-error' : undefined}
            placeholder="Engaged in the last 90 days"
            className="h-10 w-full rounded-md border border-border bg-surface px-3 text-sm text-foreground placeholder:text-muted focus:outline-none focus:ring-2 focus:ring-ring"
          />

          {touched && nameError !== undefined && (
            <p id="segment-name-error" className="mt-1 text-xs text-danger-ink">
              {nameError}
            </p>
          )}
        </div>

        <fieldset className="rounded-md border border-border p-3">
          <legend className="px-1 text-sm font-medium text-heading">Conditions</legend>

          <div className="flex flex-col gap-2">
            {conditions.map((condition, index) => (
              <ConditionRow
                key={index}
                condition={condition}
                index={index}
                canRemove={conditions.length > 1}
                onChange={(next) =>
                  setConditions((previous) => previous.map((item, at) => (at === index ? next : item)))
                }
                onRemove={() =>
                  setConditions((previous) => previous.filter((_, at) => at !== index))
                }
              />
            ))}
          </div>

          <Button
            variant="ghost"
            size="sm"
            className="mt-2"
            onClick={() => setConditions((previous) => [...previous, blankCondition()])}
          >
            <Plus className="h-3.5 w-3.5" aria-hidden="true" />
            Add condition
          </Button>

          {touched && ruleError !== undefined && (
            <p className="mt-2 text-xs text-danger-ink">{ruleError}</p>
          )}
        </fieldset>

        {/* The live count. `aria-live` because it changes without the user acting on it directly, and
            somebody using a screen reader would otherwise never learn the rule matched nobody. */}
        <div
          aria-live="polite"
          className="rounded-md border border-border bg-surface-2 px-3 py-2 text-sm"
        >
          {rule.conditions.length === 0 ? (
            <span className="text-muted">Add a condition to see how many contacts match.</span>
          ) : preview.isPending ? (
            <span className="text-muted">Counting…</span>
          ) : preview.isError ? (
            <span className="text-danger-ink">{describeError(preview.error).message}</span>
          ) : (
            <>
              <span className="font-medium text-heading">
                {preview.data.total.toLocaleString()} contacts match
              </span>

              {preview.data.sampleAddresses.length > 0 && (
                <span className="mt-1 block truncate text-xs text-muted">
                  For example: {preview.data.sampleAddresses.slice(0, 3).join(', ')}
                </span>
              )}
            </>
          )}
        </div>

        {save.isError && (
          <p role="alert" className="rounded-md bg-danger-bg px-3 py-2 text-sm text-danger-ink">
            {describeError(save.error).message}
          </p>
        )}
      </form>
    </Modal>
  );
}

function ConditionRow({
  condition,
  index,
  canRemove,
  onChange,
  onRemove,
}: {
  condition: SegmentCondition;
  index: number;
  canRemove: boolean;
  onChange: (next: SegmentCondition) => void;
  onRemove: () => void;
}) {
  const operators = operatorsFor(condition.field);

  return (
    <div className="flex flex-wrap items-end gap-2">
      <div className="min-w-36 flex-1 basis-36">
        <label htmlFor={`field-${index}`} className="sr-only">
          Condition {index + 1} field
        </label>

        <select
          id={`field-${index}`}
          value={condition.field}
          onChange={(event) => {
            const field = event.target.value as SegmentField;
            // The operator is reset when the field changes, because an operator valid for a date is
            // not valid for a number — offering a stale one would send the server a pairing it rejects.
            onChange({ ...condition, field, operator: operatorsFor(field)[0].value, value: null });
          }}
          className="h-10 w-full rounded-md border border-border bg-surface px-2 text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-ring"
        >
          {fieldGroups.map((group) => (
            <optgroup key={group.label} label={group.label}>
              {group.fields.map((field) => (
                <option key={field.value} value={field.value}>
                  {field.label}
                </option>
              ))}
            </optgroup>
          ))}
        </select>
      </div>

      <div className="min-w-36 flex-1 basis-36">
        <label htmlFor={`operator-${index}`} className="sr-only">
          Condition {index + 1} operator
        </label>

        <select
          id={`operator-${index}`}
          value={condition.operator}
          onChange={(event) =>
            onChange({ ...condition, operator: event.target.value as SegmentOperator })
          }
          className="h-10 w-full rounded-md border border-border bg-surface px-2 text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-ring"
        >
          {operators.map((operator) => (
            <option key={operator.value} value={operator.value}>
              {operator.label}
            </option>
          ))}
        </select>
      </div>

      {needsValue(condition.operator) && (
        <div className="min-w-36 flex-1 basis-36">
          <label htmlFor={`value-${index}`} className="sr-only">
            Condition {index + 1} value
          </label>

          <input
            id={`value-${index}`}
            type="text"
            value={condition.value ?? ''}
            onChange={(event) => onChange({ ...condition, value: event.target.value })}
            placeholder="value"
            className="h-10 w-full rounded-md border border-border bg-surface px-2 text-sm text-foreground placeholder:text-muted focus:outline-none focus:ring-2 focus:ring-ring"
          />
        </div>
      )}

      {condition.field === 'CustomField' && (
        <div className="min-w-36 flex-1 basis-36">
          <label htmlFor={`key-${index}`} className="sr-only">
            Condition {index + 1} custom field key
          </label>

          <input
            id={`key-${index}`}
            type="text"
            value={condition.customFieldKey ?? ''}
            onChange={(event) => onChange({ ...condition, customFieldKey: event.target.value })}
            placeholder="plan_tier"
            className="h-10 w-full rounded-md border border-border bg-surface px-2 text-sm text-foreground placeholder:text-muted focus:outline-none focus:ring-2 focus:ring-ring"
          />
        </div>
      )}

      {canRemove && (
        <button
          type="button"
          onClick={onRemove}
          aria-label={`Remove condition ${index + 1}`}
          className="flex h-11 w-11 shrink-0 items-center justify-center rounded-md text-muted hover:bg-chip focus:outline-none focus:ring-2 focus:ring-ring"
        >
          <Trash2 className="h-4 w-4" aria-hidden="true" />
        </button>
      )}
    </div>
  );
}

function blankCondition(): SegmentCondition {
  return { field: 'Email', operator: 'IsSet', value: null, customFieldKey: null, campaignId: null };
}

/** A condition is only sent once it is complete — a half-filled row must not narrow the preview. */
function isUsable(condition: SegmentCondition): boolean {
  if (condition.field === 'CustomField' && (condition.customFieldKey ?? '') === '') return false;

  return !needsValue(condition.operator) || (condition.value ?? '') !== '';
}
